import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utils'
import ListItem from '@tiptap/extension-list-item'
import { TextStyle, Color } from '@tiptap/extension-text-style'
import TextAlign from '@tiptap/extension-text-align'
import { TaskItem, TaskList } from '@tiptap/extension-list'
import Highlight from '@tiptap/extension-highlight'
import { CharacterCount, Placeholder } from '@tiptap/extensions'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import type {
  EditorEvents,
  Extensions,
  Editor as TipTapEditor,
} from '@tiptap/react'
import type { EditorAttributes } from './lib/types'

import { Marker } from './tiptap/Marker'
import { Icons } from './tiptap/Icons'
import { SlashCommand } from './tiptap/SlashCommand'
import { Submit } from './tiptap/Submit'
import { EditorMenuBar } from './EditorMenuBar'
import { Markup } from './Markup'
import Mention from './tiptap/Mention'

import iconSuggestion from './IconSuggestion'
import mentionSuggestion from './MentionSuggestion'
import { CUSTOM_ICONS, ExtensionsEnum } from './constants'
import { useDocumentState } from '@/state'
import { useToolNameExtension } from '@/components/editor/tiptap/hooks/useToolNameExtension'
import { TOOL_NAME_REFRESH_META_KEY } from '@/components/editor/tiptap/ToolName'

const baseExtensions = [
  // @ts-expect-error - types are wrong
  TextStyle.configure({ types: [ListItem.name] }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
    link: {
      openOnClick: true,
      autolink: true,
      linkOnPaste: true,
    },
  }),
]

const DEFAULT_EXTENSIONS_LIST = [
  ExtensionsEnum.ICONS,
  ExtensionsEnum.LINK,
  ExtensionsEnum.COLOR,
  ExtensionsEnum.UNDERLINE,
  ExtensionsEnum.TEXT_ALIGN,
  ExtensionsEnum.MARKER,
  ExtensionsEnum.TOOL_NAME,
  ExtensionsEnum.HIGHLIGHT,
  ExtensionsEnum.TASK_LIST,
  ExtensionsEnum.TASK_ITEM,
  ExtensionsEnum.MENTION,
  ExtensionsEnum.SLASH_COMMAND,
  ExtensionsEnum.TOOL_NAME,
]

const EXTENSIONS_MAP = {
  [ExtensionsEnum.ICONS]: Icons.configure({
    emojis: CUSTOM_ICONS,
    suggestion: iconSuggestion,
    HTMLAttributes: {
      class: 'annotation-icon',
    },
  }),
  [ExtensionsEnum.COLOR]: Color.configure({
    types: [TextStyle.name, ListItem.name],
  }),
  [ExtensionsEnum.TEXT_ALIGN]: TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  [ExtensionsEnum.MARKER]: Marker.configure({
    multicolor: true,
    HTMLAttributes: {
      class: 'slash-command-pill',
    },
  }),
  [ExtensionsEnum.HIGHLIGHT]: Highlight.configure({
    multicolor: true,
    HTMLAttributes: {
      class: 'highlight',
    },
  }),
  [ExtensionsEnum.TASK_LIST]: TaskList,
  [ExtensionsEnum.TASK_ITEM]: TaskItem.configure({
    nested: true,
  }),
  [ExtensionsEnum.MENTION]: Mention.configure({
    suggestion: mentionSuggestion,
  }),
}

type EditorProps = {
  className?: string
  documentPageId: string
  documentId: string
  documentVersionId?: string
  projectId: string
  disabled?: boolean
  isMultiSelecting?: boolean
  content: string
  onUpdate: (props: EditorEvents['update']) => void
  onSubmit?: (content?: string) => void
  onBlur?: (props: { editor: TipTapEditor }) => void
  onFocus?: (props: { editor: TipTapEditor }) => void
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void
  onPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void
  onPointerUp?: (event: React.PointerEvent<HTMLDivElement>) => void
  placeholder?: string
  isDragging?: boolean
  menuScale?: number
  menuOffsetY?: number
  updateBomWithSlashCommands?: boolean
  extensionsList?: Array<ExtensionsEnum>
  characterCountLimit?: number
  isTransparent?: boolean
  editorRef?: RefObject<TipTapEditor | null>
  isTextSelectionEnabled?: boolean
  backgroundColor?: string
  updateBackgroundColorHandler?: (color: string) => void
  toolBackgroundColor?: string
  dropdownZIndex?: number
}
export const Editor = ({
  className,
  documentId,
  documentVersionId,
  documentPageId,
  projectId,
  disabled,
  isMultiSelecting = false,
  content,
  onUpdate,
  onSubmit,
  onBlur,
  onFocus,
  onClick = () => {},
  onMouseDown,
  onPointerDown,
  onPointerUp,
  placeholder,
  isDragging = false,
  menuScale = 1,
  menuOffsetY = 0,
  updateBomWithSlashCommands,
  updateBackgroundColorHandler,
  characterCountLimit,
  isTransparent,
  backgroundColor,
  editorRef,
  isTextSelectionEnabled = true,
  extensionsList = DEFAULT_EXTENSIONS_LIST,
  toolBackgroundColor,
  dropdownZIndex,
}: EditorProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [readOnlyHtml, setReadOnlyHtml] = useState(content)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const { toolNameExtension, toolNameById } = useToolNameExtension({
    documentVersionId,
    toolBackgroundColor,
  })
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  })

  const isHardReadOnly = useMemo(
    () => Boolean(disabled || isAddingElement),
    [disabled, isAddingElement],
  )
  const isToolNameExtensionEnabled = useMemo(
    () => extensionsList.includes(ExtensionsEnum.TOOL_NAME),
    [extensionsList],
  )
  const shouldPreventEditorPointerDefault = useCallback(
    (event: React.MouseEvent<Element> | React.PointerEvent<Element>) => {
      if (isTextSelectionEnabled) return false

      const target = event.target
      return !(
        target instanceof HTMLElement &&
        target.closest('[contenteditable="true"]')
      )
    },
    [isTextSelectionEnabled],
  )

  const updateMenuPosition = useCallback(() => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const nextTop = rect.top + window.scrollY - 48 * menuScale
    const nextLeft = rect.left + window.scrollX + rect.width / 2

    // Only update state if position has changed (with threshold to avoid infinite loops)
    setMenuPos((previousMenuPos) => {
      if (
        Math.abs(previousMenuPos.top - nextTop) < 0.5 &&
        Math.abs(previousMenuPos.left - nextLeft) < 0.5
      ) {
        return previousMenuPos // Return same reference to prevent re-render
      }
      return {
        top: nextTop,
        left: nextLeft,
      }
    })
  }, [menuScale])

  useEffect(() => {
    if (!showMenu || isDragging || isHardReadOnly || isMultiSelecting) return
    updateMenuPosition()

    const onScroll = () => updateMenuPosition()
    const onResize = () => updateMenuPosition()

    let raf = 0
    const tick = () => {
      updateMenuPosition()
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [
    showMenu,
    isDragging,
    isHardReadOnly,
    isMultiSelecting,
    updateMenuPosition,
  ])

  const extensions = useMemo(() => {
    const e: Extensions = [...baseExtensions]

    extensionsList.forEach((extension) => {
      if (EXTENSIONS_MAP[extension]) {
        e.push(EXTENSIONS_MAP[extension])
      }
    })

    if (extensionsList.includes(ExtensionsEnum.SLASH_COMMAND)) {
      e.push(
        SlashCommand.configure({
          slashComponentProps: {
            updateBomOnSelect: updateBomWithSlashCommands,
          },
        }),
      )
    }

    if (extensionsList.includes(ExtensionsEnum.TOOL_NAME)) {
      e.push(toolNameExtension)
    }

    if (placeholder) {
      e.push(
        Placeholder.configure({
          placeholder,
        }),
      )
    }

    if (onSubmit) {
      e.push(
        Submit.configure({
          onSubmit,
        }),
      )
    }

    if (characterCountLimit) {
      e.push(
        CharacterCount.configure({
          limit: characterCountLimit,
        }),
      )
    }

    return e
  }, [
    placeholder,
    extensionsList,
    characterCountLimit,
    onSubmit,
    updateBomWithSlashCommands,
    toolNameExtension,
  ])

  const editorAttributes: EditorAttributes = {
    'data-testid': 'tip-tap-editor-content',
    documentPageId,
    projectId,
    documentId,
    class: cn('tiptap ProseMirror', {
      '!bg-transparent': isTransparent || backgroundColor,
    }),
  }

  const editor = useEditor({
    extensions,
    content,
    editable: !isHardReadOnly,
    onFocus: ({ event }) => {
      if (isAddingElement) {
        const target = event.target as any
        target?.blur()
      }
    },
    editorProps: {
      attributes: editorAttributes,
    },
    onUpdate,
  })

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!isHardReadOnly)
  }, [editor, isHardReadOnly])

  useEffect(() => {
    if (!editor) return
    // In multi-select mode, keep the normal editor rendering (placeholder/content),
    // but force-blur to prevent text edits.
    if (isMultiSelecting && editor.isFocused) {
      editor.commands.blur()
      setShowMenu(false)
    }
  }, [editor, isMultiSelecting])

  // Whenever the `content` prop changes, push it into the editor
  useEffect(() => {
    if (!editor) return

    if (editor.getHTML() !== content) {
      editor.commands.setContent(content, {
        emitUpdate: false,
      })
    }
    setReadOnlyHtml(editor.getHTML())
  }, [content, editor])

  // Ensure tiptap editor always has the latest content
  useEffect(() => {
    if (!editor) return

    const syncReadOnlyHtml = ({ editor }: { editor: TipTapEditor }) => {
      setReadOnlyHtml(editor.getHTML())
    }

    editor.on('update', syncReadOnlyHtml)

    return () => {
      editor.off('update', syncReadOnlyHtml)
    }
  }, [editor])

  useEffect(() => {
    if (!editor) return

    if (!isToolNameExtensionEnabled) return

    const html = editor.getHTML()
    if (!html.includes('data-toolid=')) return

    /**
     * Tool names arrive asynchronously after slash insertion.
     * Trigger ToolName plugin refresh without re-setting editor HTML so we do
     * not reset selection/caret while the user is typing.
     */
    editor.view.dispatch(
      editor.state.tr.setMeta(TOOL_NAME_REFRESH_META_KEY, true),
    )
  }, [editor, isToolNameExtensionEnabled, toolNameById])

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor
    }
  }, [editorRef, editor])

  const handleBlur = useCallback(
    (event) => {
      if (ref.current && ref.current.contains(event.relatedTarget)) {
        return
      }
      setShowMenu(false)

      if (editor && typeof onBlur === 'function') {
        onBlur({ editor })
      }
    },
    [editor, onBlur],
  )

  const handleFocus = useCallback(() => {
    if (!isHardReadOnly && !isMultiSelecting) {
      setShowMenu(true)
    }

    if (editor && typeof onFocus === 'function') {
      onFocus({ editor })
    }
  }, [isHardReadOnly, isMultiSelecting, editor, onFocus])

  if (!editor) {
    return null
  }

  if (disabled) {
    return (
      <div
        className="relative h-full w-full rounded-md"
        style={{ background: backgroundColor }}
        onMouseDown={onMouseDown}
        onPointerUp={onPointerUp}
        onPointerDown={onPointerDown}
      >
        <div
          className={cn(
            'cursor-text flex min-h-[45px] h-full w-full rounded-md bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 flex-1 overflow-auto',
            className,
          )}
        >
          <div className="tiptap ProseMirror !bg-transparent">
            <Markup html={readOnlyHtml} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="relative h-full w-full rounded-md"
      ref={ref}
      style={{ background: backgroundColor }}
    >
      <EditorContent
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={onClick}
        onMouseDown={(e) => {
          if (isMultiSelecting) {
            e.preventDefault()
            e.stopPropagation()
          }
          if (shouldPreventEditorPointerDefault(e)) {
            e.preventDefault()
          }
          if (showMenu) {
            e.stopPropagation()
          }
          if (typeof onMouseDown === 'function') {
            onMouseDown(e)
          }
        }}
        onMouseMove={(e) => {
          if (!isTextSelectionEnabled) {
            e.preventDefault()
          }
        }}
        onPointerDown={(e) => {
          if (shouldPreventEditorPointerDefault(e)) {
            e.preventDefault()
          }
          if (showMenu) {
            e.stopPropagation()
          }
          if (typeof onPointerDown === 'function') {
            onPointerDown(e)
          }
        }}
        onPointerUp={onPointerUp}
        className={cn(
          'cursor-text flex min-h-[45px] h-full w-full rounded-md bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 flex-1 overflow-auto',
          { 'select-none': !isTextSelectionEnabled },
          className,
        )}
        editor={editor}
        placeholder={placeholder}
        data-testid="tip-tap-editor"
      >
        {showMenu &&
          !isDragging &&
          createPortal(
            <div
              className="fixed flex justify-center"
              style={{
                top: `${menuPos.top + menuOffsetY}px`,
                left: `${menuPos.left}px`,
                transform: `translateX(-50%) scale(${menuScale})`,
                zIndex: 999999,
                pointerEvents: 'auto',
              }}
            >
              <EditorMenuBar
                editor={editor}
                extensionsList={extensionsList}
                backgroundColor={backgroundColor}
                updateBackgroundColorHandler={updateBackgroundColorHandler}
                dropdownZIndex={dropdownZIndex}
                onClickOutside={() => {
                  setShowMenu(false)
                  editor.commands.blur()
                }}
              />
            </div>,
            document.body,
          )}
      </EditorContent>
    </div>
  )
}
